/**
 * Centralized application logger
 * ================================
 *
 * Import:  import { logger } from '@/lib/logger';
 *
 * Levels:
 *   logger.debug(...) — Dev-only. Flow tracing, intermediate values.
 *   logger.info(...)  — Dev-only. Milestones (auth events, navigation).
 *   logger.warn(...)  — Always emitted. Recoverable issues.
 *   logger.error(msg, err?) — Always emitted, PII-sanitized in production.
 *
 * Rules — NEVER log objects containing these keys even in dev:
 *   client_id, helper_id, *_address, *_phone, *_email,
 *   *_token, password, secret
 * Use sanitize() to strip them before logging if needed.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const isProduction = import.meta.env.PROD;
const isDevelopment = import.meta.env.DEV;

/** Keys whose values must never appear in logs */
const SENSITIVE_KEYS = new Set([
  'client_id',
  'helper_id',
  'client_address',
  'service_address',
  'mobile_money_phone',
  'phone',
  'email',
  'access_token',
  'refresh_token',
  'provider_token',
  'password',
  'secret',
  'special_instructions',
]);

/**
 * Strips sensitive keys from an object before logging.
 * Works one level deep — nested objects are redacted entirely
 * if they contain sensitive keys.
 */
export function sanitize(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (SENSITIVE_KEYS.has(k)) {
      result[k] = '[redacted]';
    } else {
      result[k] = v;
    }
  }
  return result;
}

export const logger = {
  debug: (...args: unknown[]): void => {
    // eslint-disable-next-line no-console -- this is the logger's sanctioned dev-only debug sink
    if (isDevelopment) console.debug('[DEBUG]', ...args);
  },

  info: (...args: unknown[]): void => {
    // eslint-disable-next-line no-console -- this is the logger's sanctioned dev-only info sink
    if (isDevelopment) console.info('[INFO]', ...args);
  },

  warn: (...args: unknown[]): void => {
    console.warn('[WARN]', ...args);
  },

  /**
   * Log an error.
   * - Production: emits only the message string + error.message (no objects).
   * - Development: emits the full error for debugging.
   */
  error: (message: string, error?: unknown): void => {
    if (isProduction) {
      const safeDetail =
        error instanceof Error ? error.message : 'Unknown error';
      console.error(`[ERROR] ${message}`, safeDetail);
    } else {
      console.error(`[ERROR] ${message}`, error);
    }
  },
};
